import { useState } from "react";
import { toast } from "sonner";
import { api } from "../lib/api";
import { MapPin, Pencil, CalendarDays, Mail } from "lucide-react";

const STATUS = {
  active: ["Actif", "bg-[#7B8A72] text-white"], cancel_scheduled: ["Résiliation programmée", "bg-[#C98A2B] text-white"],
  payment_failed: ["Paiement en attente", "bg-[#B23B3B] text-white"], canceled: ["Résilié", "bg-[#DCCBBA]"],
  prepaid_active: ["En cours", "bg-[#7B8A72] text-white"], gift_active: ["En cours (cadeau)", "bg-[#7B8A72] text-white"],
  gift_ended: ["Terminé", "bg-[#DCCBBA]"], prepaid_done: ["Terminé", "bg-[#DCCBBA]"],
};
const MONTHS = ["janvier", "février", "mars", "avril", "mai", "juin", "juillet", "août", "septembre", "octobre", "novembre", "décembre"];
const monthLabel = (mk) => mk ? `${MONTHS[parseInt(mk.slice(5, 7), 10) - 1]} ${mk.slice(0, 4)}` : "";
const fmtDate = (iso) => iso ? new Date(iso).toLocaleDateString("fr-FR", { day: "numeric", month: "long", year: "numeric" }) : "—";

function AddressForm({ token, sub, onDone }) {
  const a = sub.address || {};
  const [f, setF] = useState({ first_name: sub.first_name || "", last_name: sub.last_name || "", line1: a.line1 || "", line2: a.line2 || "", postal_code: a.postal_code || "", city: a.city || "", country: a.country || "FR" });
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  async function save(e) {
    e.preventDefault(); setBusy(true);
    try { await api.post("/client/mailclub/address", { token, ...f }); toast.success("Adresse mise à jour — merci !"); onDone(); }
    catch (err) { toast.error(err.response?.data?.detail || "Erreur"); } finally { setBusy(false); }
  }
  const I = (k, ph, extra = {}) => <input data-testid={`club-addr-${k}`} value={f[k]} onChange={set(k)} placeholder={ph} className="field-input" {...extra}/>;
  return (
    <form data-testid="club-address-form" onSubmit={save} className="grid sm:grid-cols-2 gap-3 mt-4 bg-white/60 p-4 border border-[#E8DCC8]">
      {I("first_name", "Prénom")}{I("last_name", "Nom")}
      <div className="sm:col-span-2">{I("line1", "Adresse", { required: true })}</div>
      <div className="sm:col-span-2">{I("line2", "Complément (facultatif)")}</div>
      {I("postal_code", "Code postal", { required: true })}{I("city", "Ville", { required: true })}
      {I("country", "Pays (FR, BE, CH…)", { required: true, maxLength: 2 })}
      <div className="sm:col-span-2 flex gap-3"><button data-testid="club-addr-save" disabled={busy} className="btn-primary !py-2">{busy ? "…" : "Enregistrer"}</button><button type="button" onClick={onDone} className="btn-outline !py-2">Annuler</button></div>
    </form>
  );
}

export function EspaceClub({ token, subs, onRefresh }) {
  const [editing, setEditing] = useState(null);
  const [busy, setBusy] = useState(false);
  if (!subs?.length) return null;

  async function cancel() {
    if (!window.confirm("Résilier ton abonnement mensuel ? Tu recevras encore l'enveloppe déjà réglée, puis plus aucun prélèvement.")) return;
    setBusy(true);
    try { await api.post("/mailclub/cancel", { token }); toast.success("Résiliation enregistrée. Merci pour ce bout de chemin 💌"); onRefresh(); }
    catch (e) { toast.error(e.response?.data?.detail || "Erreur"); } finally { setBusy(false); }
  }

  return (
    <section data-testid="espace-club" className="mb-14">
      <p className="font-hand text-xl text-[#A3876A]">Ton Club de Lettres</p>
      <h2 className="font-serif text-3xl mb-6">Mon abonnement</h2>
      <div className="space-y-6">
        {subs.map((s, i) => {
          const [label, cls] = STATUS[s.status] || [s.status, "bg-[#F5F0E6]"];
          const a = s.address || {};
          const mailings = Object.entries(s.mailings || {}).sort((x, y) => y[0].localeCompare(x[0]));
          const key = s._prepaid_id || s._gift_id || `m${i}`;
          return (
            <div key={key} data-testid={`club-sub-${i}`} className="bg-[#F5F0E6] p-6 md:p-8 border border-[#E8DCC8]">
              <div className="flex flex-wrap items-start justify-between gap-3">
                <p className="font-serif text-xl">{s.plan_label}</p>
                <span data-testid={`club-status-${i}`} className={`text-xs px-3 py-1 ${cls}`}>{label}</span>
              </div>
              <div className="grid sm:grid-cols-2 gap-4 mt-5 text-sm text-[#4A4036]">
                <p className="flex items-start gap-2"><CalendarDays size={15} className="text-[#A3876A] mt-0.5 shrink-0"/>
                  <span data-testid={`club-next-${i}`}>
                    {s.plan === "prepaid" || s.is_gift
                      ? <>Enveloppes de <strong>{monthLabel(s.coverage_start)}</strong> à <strong>{monthLabel(s.coverage_end)}</strong><br/><span className="text-[#7C7064]">Aucun renouvellement automatique.</span></>
                      : s.status === "cancel_scheduled"
                        ? <>Fin de l'abonnement le <strong>{fmtDate(s.current_period_end)}</strong><br/><span className="text-[#7C7064]">Plus aucun prélèvement.</span></>
                        : s.status === "canceled"
                          ? <>Abonnement résilié{ s.canceled_at && <> le {fmtDate(s.canceled_at)}</>}</>
                          : <>Prochain renouvellement le <strong>{fmtDate(s.current_period_end)}</strong><br/><span className="text-[#7C7064]">Chaque paiement vaut pour l'enveloppe du mois suivant, expédiée vers le 10.</span></>}
                  </span></p>
                <div className="flex items-start gap-2"><MapPin size={15} className="text-[#A3876A] mt-0.5 shrink-0"/>
                  <div>
                    <span data-testid={`club-address-${i}`}>{[a.line1, a.line2, `${a.postal_code || ""} ${a.city || ""}`.trim(), a.country].filter(Boolean).join(", ") || "Adresse non renseignée"}</span>
                    {s.status !== "canceled" && <button data-testid={`club-edit-address-${i}`} onClick={() => setEditing(editing === key ? null : key)} className="ml-2 inline-flex items-center gap-1 text-xs text-[#A3876A] underline"><Pencil size={11}/> Modifier</button>}
                  </div></div>
              </div>
              {editing === key && <AddressForm token={token} sub={s} onDone={() => { setEditing(null); onRefresh(); }}/>}
              {mailings.length > 0 && (
                <div className="mt-5">
                  <p className="text-xs uppercase tracking-wider text-[#7C7064] mb-2 flex items-center gap-1"><Mail size={12}/> Mes enveloppes</p>
                  <ul className="text-sm space-y-1">
                    {mailings.map(([mk, ml]) => <li key={mk} className="flex justify-between border-b border-[#E8DCC8]/70 py-1"><span>{monthLabel(mk)}</span>
                      <span className="text-[#7C7064]">{ml.status === "sent" ? `Expédiée le ${fmtDate(ml.sent_at)}` : ml.status === "prepared" ? "En préparation" : "À venir"}</span></li>)}
                  </ul>
                </div>
              )}
              {s.can_cancel && <button data-testid={`club-cancel-${i}`} disabled={busy} onClick={cancel} className="mt-6 text-sm text-[#7C7064] underline hover:text-[#B23B3B]">Résilier mon abonnement mensuel</button>}
            </div>
          );
        })}
      </div>
    </section>
  );
}
